import type {
  FormDefinitionRequest,
  FormDefinitionResponse,
  FormFieldRequest,
  FormFieldSimpleResponse,
} from 'src/api/ProactApiClient';
import {
  FormFieldTypeEnum,
  FormFieldTypeValidationEnum,
} from 'src/api/ProactApiClient';
import {tryParse} from 'src/utilities/objectUtils';
import type {
  FormFieldTypeV2,
  IFormDataV2,
  IFormFieldV2,
  IFormSettings,
  ValidationTypeV2,
} from './FormV2';
import {RICHTEXT_MAX_LENGTH_DEFAULT, TEXT_MAX_LENGTH_DEFAULT} from './FormV2';
import {buildQualifiedFieldName} from './formFieldQualifiedName';

// Map form field type from enum to string type
const typeMap: Record<FormFieldTypeEnum, FormFieldTypeV2> = {
  [FormFieldTypeEnum.Invalid]: 'text',
  [FormFieldTypeEnum.Text]: 'text',
  [FormFieldTypeEnum.Date]: 'date',
  [FormFieldTypeEnum.Time]: 'text',
  [FormFieldTypeEnum.DateTime]: 'date',
  [FormFieldTypeEnum.DropdownSingle]: 'select',
  [FormFieldTypeEnum.RichText]: 'richtext',
  [FormFieldTypeEnum.FileUpload]: 'file',
  [FormFieldTypeEnum.Paragraph]: 'paragraph',
  [FormFieldTypeEnum.Divider]: 'divider',
  [FormFieldTypeEnum.Subform]: 'subform',
  [FormFieldTypeEnum.Scale]: 'scale',
  [FormFieldTypeEnum.Heading]: 'heading',
  [FormFieldTypeEnum.DropdownSingleAutocomplete]: 'autocomplete',
  [FormFieldTypeEnum.DropdownMultiModal]: 'select-multiple',
  [FormFieldTypeEnum.DropdownSinglePersonnel]: 'person',
  [FormFieldTypeEnum.DropdownMultiPersonnelModal]: 'person-select-multiple',
  [FormFieldTypeEnum.Signature]: 'signature',
  [FormFieldTypeEnum.DrawingPad]: 'drawing-pad',
  [FormFieldTypeEnum.PhoneNumber]: 'phone',
  [FormFieldTypeEnum.AssetModal]: 'asset',
  [FormFieldTypeEnum.Switch]: 'switch',
  [FormFieldTypeEnum.Number]: 'number',
  [FormFieldTypeEnum.VideoSelect]: 'video-select',
  [FormFieldTypeEnum.ProjectSelect]: 'project',
  [FormFieldTypeEnum.Measurement]: 'measurement',
};

export function mapFormFieldSimpleResponseToFormFieldV2(
  response: FormFieldSimpleResponse
): IFormFieldV2 {
  const subFormFieldId = response.subFormFieldId ?? undefined;
  const subFormFieldInvariantId = response.subFormFieldInvariantId ?? undefined;

  const field: IFormFieldV2 = {
    invariantId: response.invariantId,
    subFormFieldId,
    subFormFieldInvariantId,
    name: subFormFieldInvariantId
      ? buildQualifiedFieldName(subFormFieldInvariantId, response.invariantId!)
      : response.invariantId!,
    label: response.label,
    type: typeMap[response.formFieldType],
    required: response.required,
    description: response.description,
    labelTooltip: response.labelTooltip ?? undefined,
    size: response.size as 'xs' | 'sm' | 'md' | 'lg' | 'xl',

    builderOptions: {
      id: response.id,
      version: response.version,
      slug: response.slug,
      subFormDefinitionId: response.subFormDefinitionId,
      subFormType: response.subFormType,
      order: response.order,
      defaultValue: response.defaultValue,
      shortLabel: response.shortLabel,
      isComplianceEventField: response.isComplianceEventField,
      complianceEventTriggerValue: response.complianceEventTriggerValue,
      selectOptions: response.selectOptions,
      isFailureTriggerField: response.isFailureTriggerField,
      failureTriggerValue: response.failureTriggerValue,
      referenceImageKey: response.referenceImageKey,
      referenceImageUrl: response.referenceImageUrl,
    },

    options: response.selectOptions,

    visibility: {
      isDefaultHidden: response.visibilityOptions?.isDefaultHidden,
      rules: response.visibilityOptions?.rules,
    },

    textboxOptions: response.textboxOptions
      ? {
          validationType: mapValidationType(
            response.textboxOptions.validationType
          ),
          multiline: response.textboxOptions.multiline,
        }
      : undefined,

    switchOptions: response.switchOptions
      ? {
          positiveText: response.switchOptions.positiveText,
          negativeText: response.switchOptions.negativeText,
          positiveColor: response.switchOptions.positiveColor,
          negativeColor: response.switchOptions.negativeColor,
        }
      : undefined,

    fileUploadOptions: response.fileUploadOptions
      ? {
          allowMultiple: response.fileUploadOptions.allowMultiple,
          allowedFileTypes: response.fileUploadOptions.allowedFileTypes
            ?.split(',')
            .map(type => type.trim()),
          maxFileSize: response.fileUploadOptions.maxFileSize,
          isHideUploadSuccessEnabled:
            response.fileUploadOptions.isHideUploadSuccessEnabled,
          renderUploadsAsImage: response.fileUploadOptions.renderUploadsAsImage,
        }
      : undefined,

    assetOptions: response.assetOptions
      ? {
          rootAssetId: response.assetOptions.rootAssetId,
          includeLeaves: response.assetOptions.includeLeaves,
          requireLeaf: response.assetOptions.requireLeaf,
          showFullPath: response.assetOptions.showFullPath,
        }
      : undefined,

    personOptions: response.personOptions
      ? {
          hideSelectAll: response.personOptions.hideSelectAll,
          includeInactive: response.personOptions.includeInactive,
        }
      : undefined,

    subFormOptions: response.subFormOptions
      ? {
          subFormDefinitionId: response.subFormOptions.subFormDefinitionId,
          showPreviewComponent: response.subFormOptions.showPreviewComponent,
        }
      : undefined,

    scaleOptions: response.scaleOptions
      ? {
          leastValueText: response.scaleOptions.leastValueText,
          mostValueText: response.scaleOptions.mostValueText,
        }
      : undefined,

    // Newtonsoft serializes unset decimal?/int? members as explicit null;
    // normalize to undefined so a NumberInput bound to this doesn't treat a
    // missing bound as a 0-value clamp.
    measurementOptions: response.measurementOptions
      ? {
          minValue: response.measurementOptions.minValue ?? undefined,
          maxValue: response.measurementOptions.maxValue ?? undefined,
          decimalPlaces: response.measurementOptions.decimalPlaces ?? undefined,
          units: response.measurementUnits ?? undefined,
        }
      : undefined,

    // Newtonsoft serializes unset int?/string? members as explicit null;
    // normalize to undefined so downstream consumers (e.g. Mantine NumberInput
    // clampBehavior) don't treat them as 0-bound constraints.
    validation: response.validationOptions
      ? {
          min: response.validationOptions.min ?? undefined,
          max: response.validationOptions.max ?? undefined,
          minLength: response.validationOptions.minLength ?? undefined,
          maxLength:
            response.validationOptions.maxLength ??
            (typeMap[response.formFieldType] === 'richtext'
              ? RICHTEXT_MAX_LENGTH_DEFAULT
              : TEXT_MAX_LENGTH_DEFAULT),
          pattern: response.validationOptions.pattern ?? undefined,
          patternError: response.validationOptions.patternError ?? undefined,
          customError: response.validationOptions.customError ?? undefined,
        }
      : undefined,
  };

  return field;
}

function mapValidationType(
  type?: FormFieldTypeValidationEnum
): ValidationTypeV2 | undefined {
  if (!type) return undefined;

  const typeMap: Record<FormFieldTypeValidationEnum, ValidationTypeV2> = {
    [FormFieldTypeValidationEnum.Invalid]: 'safe',
    [FormFieldTypeValidationEnum.Name]: 'name',
    [FormFieldTypeValidationEnum.Email]: 'email',
    [FormFieldTypeValidationEnum.Address]: 'address',
    [FormFieldTypeValidationEnum.Phone]: 'safe', // No direct mapping
    [FormFieldTypeValidationEnum.Number]: 'safe', // No direct mapping
    [FormFieldTypeValidationEnum.Url]: 'url',
    [FormFieldTypeValidationEnum.Percent]: 'percent',
    [FormFieldTypeValidationEnum.Currency]: 'currency',
    [FormFieldTypeValidationEnum.DecimalNumber]: 'decimal',
    [FormFieldTypeValidationEnum.Video]: 'safe', // No direct mapping
    [FormFieldTypeValidationEnum.Audio]: 'safe', // No direct mapping
    [FormFieldTypeValidationEnum.Image]: 'safe', // No direct mapping
    [FormFieldTypeValidationEnum.Time]: 'time',
  };

  return typeMap[type];
}

export function mapFormDefinitionResponseToFormSettingsV2(
  response: FormDefinitionResponse
): IFormSettings {
  return {
    name: response.name,
    title: response.title,
    subformOnly: response.subformOnly,
    extensionOnly: response.extensionOnly,
    identifier: response.uniqueIdentifier,
    triggerAnalyticSafetyEvent: response.triggerAnalyticSafetyEvent,
    failable: response.failable,
    accessAllowEditAfterPublish: response.accessAllowEditAfterPublish ?? false,
    accessAllowSupervisors: response.accessAllowSupervisors ?? false,
    accessAllowUserGroups:
      response.accessAllowUserGroups.map(i => i.userGroupId) ?? [],
    metadata: {
      anonymousSubmission: response.metadata?.anonymousSubmission ?? false,
      description: response.metadata?.description,
      cache: tryParse(response.metadata?.cache),
      enableFormIdentifierField:
        response.metadata?.enableFormIdentifierField ?? true,
    },
    active: response.active,
    version: response.version.toString(),
    mode: 'edit',
    lockIdentifier: !!response.uniqueIdentifier,
    projectId: response.projectId ?? null,
    categoryId: response.categoryId ?? null,
    sensitiveData: response.sensitiveData ?? false,
  } as IFormSettings;
}

export function mapFormFieldsToFormFieldRequests(
  formFields: IFormFieldV2[]
): FormFieldRequest[] {
  return formFields.map((field, index) => {
    // Map form field type from V2 type to enum
    const formFieldType = mapFormFieldTypeV2ToEnum(field.type);

    return {
      // Required fields from FormFieldRequest
      formFieldType,
      htmlName: field.name,
      label: field.label,
      required: field.required ?? false,

      // Optional fields
      invariantId: field.invariantId,
      active: true, // Default to true for new fields
      subFormDefinitionId: field.builderOptions?.subFormDefinitionId,
      subFormType: field.builderOptions?.subFormType,
      shortLabel: field.builderOptions?.shortLabel,
      isComplianceEventField: field.builderOptions?.isComplianceEventField,
      complianceEventTriggerValue:
        field.builderOptions?.complianceEventTriggerValue,
      isFailureTriggerField: field.builderOptions?.isFailureTriggerField,
      failureTriggerValue: field.builderOptions?.failureTriggerValue,
      referenceImageKey: field.builderOptions?.referenceImageKey,
      referenceImageUrl: field.builderOptions?.referenceImageUrl,
      description: field.description,
      labelTooltip: field.labelTooltip,
      defaultValue: field.builderOptions?.defaultValue,
      order: field.builderOptions?.order ?? index,
      slug: field.builderOptions?.slug,
      size: field.size,

      // Options objects
      textboxOptions: {
        validationType: mapValidationTypeV2ToEnum(
          field.textboxOptions?.validationType
        ),
        multiline: field.textboxOptions?.multiline,
      },
      switchOptions: field.switchOptions ?? {},
      fileUploadOptions: {
        ...field.fileUploadOptions,
        allowedFileTypes: field.fileUploadOptions?.allowedFileTypes?.join(','),
      },
      assetOptions: field.assetOptions ?? {},
      personOptions: field.personOptions ?? {},
      subFormOptions: field.subFormOptions ?? {},
      scaleOptions: field.scaleOptions ?? {},
      measurementOptions: {
        minValue: field.measurementOptions?.minValue,
        maxValue: field.measurementOptions?.maxValue,
        decimalPlaces: field.measurementOptions?.decimalPlaces,
      },
      measurementUnits: field.measurementOptions?.units,

      // Validation and visibility
      validationOptions: {
        min: field.validation?.min,
        max: field.validation?.max,
        minLength: field.validation?.minLength,
        maxLength: field.validation?.maxLength,
        pattern: field.validation?.pattern,
        patternError: field.validation?.patternError,
        customError: field.validation?.customError,
      },
      visibilityOptions: field.visibility ?? {
        rules: [],
      },

      // Select options
      selectOptions: field.builderOptions?.selectOptions ?? [],

      // Compliance standards
      targetComplianceStandardIds:
        field.builderOptions?.complianceStandardIds?.map(std =>
          typeof std.id === 'number' ? std.id : parseInt(std.id)
        ),
    };
  });
}

// Helper function to map FormFieldTypeV2 to FormFieldTypeEnum
function mapFormFieldTypeV2ToEnum(type: FormFieldTypeV2): FormFieldTypeEnum {
  const typeMap: Record<FormFieldTypeV2, FormFieldTypeEnum> = {
    text: FormFieldTypeEnum.Text,
    textarea: FormFieldTypeEnum.Text,
    select: FormFieldTypeEnum.DropdownSingle,
    autocomplete: FormFieldTypeEnum.DropdownSingleAutocomplete,
    date: FormFieldTypeEnum.Date,
    switch: FormFieldTypeEnum.Switch,
    number: FormFieldTypeEnum.Number,
    richtext: FormFieldTypeEnum.RichText,
    file: FormFieldTypeEnum.FileUpload,
    asset: FormFieldTypeEnum.AssetModal,
    person: FormFieldTypeEnum.DropdownSinglePersonnel,
    'person-select-multiple': FormFieldTypeEnum.DropdownMultiPersonnelModal,
    scale: FormFieldTypeEnum.Scale,
    signature: FormFieldTypeEnum.Signature,
    'drawing-pad': FormFieldTypeEnum.DrawingPad,
    phone: FormFieldTypeEnum.PhoneNumber,
    'select-multiple': FormFieldTypeEnum.DropdownMultiModal,
    paragraph: FormFieldTypeEnum.Paragraph,
    heading: FormFieldTypeEnum.Heading,
    divider: FormFieldTypeEnum.Divider,
    subform: FormFieldTypeEnum.Subform,
    'video-select': FormFieldTypeEnum.VideoSelect,
    project: FormFieldTypeEnum.ProjectSelect,
    measurement: FormFieldTypeEnum.Measurement,
    // Frontend-only field type; persisted as a comma-separated Text value.
    tags: FormFieldTypeEnum.Text,
  };

  return typeMap[type];
}

export function mapFormSettingsV2ToFormDefinitionRequest(
  formDefinitionId: number,
  version: number,
  settings: IFormSettings,
  formFields: IFormFieldV2[]
): FormDefinitionRequest {
  return {
    id: formDefinitionId,
    uniqueIdentifier: settings.identifier,
    version: version,
    active: settings.active,
    name: settings.name,
    title: settings.title,
    subformOnly: settings.subformOnly,
    extensionOnly: settings.extensionOnly,
    triggerAnalyticSafetyEvent: settings.triggerAnalyticSafetyEvent,
    failable: settings.failable,
    accessAllowEditAfterPublish: settings.accessAllowEditAfterPublish,
    accessAllowSupervisors: settings.accessAllowSupervisors,
    sensitiveData: settings.sensitiveData,
    metadata: {
      ...settings.metadata,
      cache: JSON.stringify(settings.metadata.cache),
    },
    targetFormFields: mapFormFieldsToFormFieldRequests(formFields),
    targetAccessUserGroupIds: settings.accessAllowUserGroups,
    projectId: settings.projectId,
    categoryId: settings.categoryId ?? undefined,
  } as FormDefinitionRequest;
}

// Field types whose seeded '' represents "untouched" but whose DTO target is
// a non-string scalar (number | undefined or Date | undefined). Untouched
// text/textarea fields legitimately submit '' and must not be coerced here.
const EMPTY_TO_UNDEFINED_FIELD_TYPES: FormFieldTypeV2[] = [
  'project',
  'asset',
  'person',
  'date',
  'number',
];

export const emptyToUndefined = (value: unknown): unknown =>
  value === '' ? undefined : value;

export const normalizeFormValues = (
  formMeta: IFormFieldV2[] | undefined,
  values: IFormDataV2
): IFormDataV2 => {
  if (!formMeta) return values;

  const normalized = {...values};
  for (const field of formMeta) {
    if (
      EMPTY_TO_UNDEFINED_FIELD_TYPES.includes(field.type) &&
      field.name in normalized
    ) {
      normalized[field.name] = emptyToUndefined(normalized[field.name]);
    }
  }
  return normalized;
};

export const createInitialFormStateV2 = (
  meta?: IFormFieldV2[]
): IFormDataV2 => {
  if (!meta) return {};

  // sets default values for any field with configured default value
  const fields = meta.map(field => {
    let defaultValue: unknown = field.builderOptions?.defaultValue ?? '';
    switch (field.type) {
      case 'select-multiple':
        defaultValue = [];
        break;
      case 'person-select-multiple':
        defaultValue = [];
        break;
      case 'tags':
        defaultValue = [];
        break;
      case 'switch':
        // Boolean fields start unset ('' fails boolean schemas and silently
        // blocks required switches); a builder-configured default wins.
        if (defaultValue === '') {
          defaultValue = false;
        }
        break;
      case 'measurement':
        // Object-valued field (see IFormFieldV2.measurementOptions doc) —
        // '' would desync the NumberInput/Select pair below it.
        if (defaultValue === '') {
          defaultValue = {magnitude: '', unit: ''};
        }
        break;

      default:
        break;
    }
    return {
      [field.name]: defaultValue,
    };
  });

  const data = {} as IFormDataV2;
  for (const item in fields) {
    if (item) {
      const [values] = Object.entries(fields[item]);
      if (values) {
        const [key, value] = values;
        data[key] = value;
      }
    }
  }

  return data;
};
// Helper function to map ValidationTypeV2 to FormFieldTypeValidationEnum
function mapValidationTypeV2ToEnum(
  type: ValidationTypeV2 | undefined
): FormFieldTypeValidationEnum | undefined {
  if (!type) return undefined;

  const typeMap: Record<ValidationTypeV2, FormFieldTypeValidationEnum> = {
    name: FormFieldTypeValidationEnum.Name,
    email: FormFieldTypeValidationEnum.Email,
    address: FormFieldTypeValidationEnum.Address,
    url: FormFieldTypeValidationEnum.Url,
    percent: FormFieldTypeValidationEnum.Percent,
    currency: FormFieldTypeValidationEnum.Currency,
    decimal: FormFieldTypeValidationEnum.DecimalNumber,
    time: FormFieldTypeValidationEnum.Time,
    safe: FormFieldTypeValidationEnum.Invalid, // No direct mapping, use Invalid as fallback
  };

  return typeMap[type];
}
